import React, {useState, useEffect} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import axios from "axios";
import notify, {AppToastContainer} from "../../components/ACS-Dashboard/ACS-Blocks/Toast";
import "react-toastify/dist/ReactToastify.css";
import Cookies from "js-cookie";
import Loader from "../../components/ACS-Dashboard/ACS-Blocks/Loader";
import testimonialImage from "../../assets/ACS-Images/logistics-testimonial.png";
import ascendLogo from "../../assets/ACS-Images/Ascend-Logo.png";
import GoogleLogo from "../../assets/ACS-Images/Google-Logo.png";
import MicrosoftLogo from "../../assets/ACS-Images/Microsoft-Logo.png";
import AppleLogo from "../../assets/ACS-Images/Apple-Logo.png";
import SSOLogo from "../../assets/ACS-Images/SSO-Logo.png";
import { resetReduxState } from "../../redux/store";
import { getPostLoginRedirect } from "../../routes/dashboardPaths";

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    useEffect(() => {
        resetReduxState();
        localStorage.removeItem("isCollapsed");
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            notify.error({title: "Missing fields", message: "Please fill in all fields."});
            return;
        }

        setLoading(true);

        try {
            setLoading(true);
            // Stringify the payload
            const loginPayload = JSON.stringify({email, password});
            // Login request with Axios
            const loginResponse = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/auth/user/login`,
                loginPayload,  // Sending stringified JSON
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    }
                }
            );
            const loginData = loginResponse.data;

            try {
                const privilegeRes = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/auth/user/getPrivilegesByType?privilegeType=UI`,
                    {
                        headers: {
                            Authorization: `Bearer ${loginData.accessToken}`,
                            Accept: "application/json",
                        },
                        withCredentials: true,
                    }
                );

                const rawPrivileges = privilegeRes.data;
                localStorage.setItem("userPrivileges", JSON.stringify(rawPrivileges));

                const allowedMenus = rawPrivileges
                    .filter((priv) => {
                        if (priv.action !== "VIEW") return false;
                        if (typeof priv.menu === "object") return priv.menu.active === true;
                        if (typeof priv.menu === "number") return true;
                        return false;
                    })
                    .map((priv) => {
                        if (typeof priv.menu === "object") return priv.menu.name;
                        return priv.name?.split(":")[0];
                    })
                    .filter(Boolean);

                console.log("✅ allowedMenus:", allowedMenus); // ✅ now inside scope
                localStorage.setItem("allowedMenus", JSON.stringify(allowedMenus));


                // privileges: the array you provided
                function buildMenuTabStructure(privileges) {
                    const menus = {};

                    privileges.forEach(priv => {
                        const menu = priv.menu;
                        const parent = menu.parentMenu;

                        if (!parent) {
                            // Main menu
                            if (!menus[menu.name]) {
                                menus[menu.name] = {
                                    displayName: menu.displayName,
                                    icon: menu.icon,
                                    tabs: {},
                                    privilegeNames: [],
                                    actions: []
                                };
                            }
                            // Add privilege to main menu if not already present
                            if (!menus[menu.name].privilegeNames.includes(priv.name)) {
                                menus[menu.name].privilegeNames.push(priv.name);
                            }
                            if (!menus[menu.name].actions.includes(priv.action)) {
                                menus[menu.name].actions.push(priv.action);
                            }
                        } else {
                            // Sub tab under parent menu
                            const parentName = parent.name;
                            if (!menus[parentName]) {
                                menus[parentName] = {
                                    displayName: parent.displayName,
                                    icon: parent.icon,
                                    tabs: {},
                                    privilegeNames: [],
                                    actions: []
                                };
                            }
                            if (!menus[parentName].tabs[menu.name]) {
                                menus[parentName].tabs[menu.name] = {
                                    displayName: menu.displayName,
                                    icon: menu.icon,
                                    privilegeNames: [],
                                    actions: []
                                };
                            }
                            // Add privilege name and action to tab if not already present
                            if (!menus[parentName].tabs[menu.name].privilegeNames.includes(priv.name)) {
                                menus[parentName].tabs[menu.name].privilegeNames.push(priv.name);
                            }
                            if (!menus[parentName].tabs[menu.name].actions.includes(priv.action)) {
                                menus[parentName].tabs[menu.name].actions.push(priv.action);
                            }
                        }
                    });

                    return menus;
                }

// Usage:
                const menuTabStructure = buildMenuTabStructure(rawPrivileges);
                console.log(menuTabStructure);
                localStorage.setItem("menuTabStructure", JSON.stringify(menuTabStructure));

            } catch (err) {
                console.warn("Privilege fetch failed:", err);
            }


            // 2. Set auth cookies
            Cookies.set("authToken", loginData.accessToken, {
                domain: process.env.REACT_APP_COOKIE_DOMAIN,
                expires: rememberMe ? 30 : 1,
                secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                sameSite: "Lax",
            });

            Cookies.set("userData", JSON.stringify({
                id: loginData.id,
                email: loginData.email,
                name: loginData.name
            }), {
                domain: process.env.REACT_APP_COOKIE_DOMAIN,
                secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                sameSite: "Lax",
            });

            // 3. Attempt to fetch organizations with Axios
            let orgData = [];
            try {
                const orgResponse = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/auth/user/getOrganizations`,
                    {
                        headers: {
                            "Authorization": `Bearer ${loginData.accessToken}`,
                            "Accept": "application/json"
                        },
                        withCredentials: true
                    }
                );

                orgData = orgResponse.data;
            } catch (orgError) {
                console.warn("Organization fetch warning:", orgError);
            }

            // 4. Set officeCode if organizations exist
            if (orgData.length > 0) {
                // Save organization data to local storage
                localStorage.setItem("organizationData", JSON.stringify(orgData));
                const orgTypes = Array.from(
                    new Set(orgData.map(org => org.orgType?.toLowerCase()).filter(Boolean))
                );
                localStorage.setItem("organizationTypes", JSON.stringify(orgTypes));
                
                // Save first organization's office code to local storage similar to cookies
                localStorage.setItem("officeCode", orgData[0].officeCode);

                Cookies.set("officeCode", orgData[0].officeCode, {
                    domain: process.env.REACT_APP_COOKIE_DOMAIN,
                    expires: rememberMe ? 30 : 1,
                    secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                    sameSite: "Lax",
                });

                // officeCodes: [];
                // for (const org of orgData) {
                //   officeCodes.push(org.officeCode);
                // }

                const officeCodes = orgData.map(org => org.officeCode).join(",");
                console.log("🌐 Office Codes:", officeCodes);
                Cookies.set("officeCodes", officeCodes, {
                    domain: process.env.REACT_APP_COOKIE_DOMAIN,
                    expires: rememberMe ? 30 : 1,
                    secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                    sameSite: "Lax",
                });
            }


            // 5. Parse JWT expiry and store it, then navigate
            try {
                const payload = JSON.parse(atob(loginData.accessToken.split(".")[1]));
                if (payload.exp) {
                    localStorage.setItem("sessionExpiry", (payload.exp * 1000).toString());
                }
            } catch (e) {
                console.warn("Could not parse JWT expiry:", e);
            }
            resetReduxState();
            navigate(getPostLoginRedirect(location.state?.from), { replace: true });
            setLoading(false);

        } catch (error) {
            console.error("Login error:", error);
            setLoading(false);

            const errorMessage = error.response?.data?.content
                || error.message
                || "Login failed. Please try again.";

            if (!errorMessage.toLowerCase().includes("organizations")) {
                notify.error({title: "Login failed", message: errorMessage});
            }
        }
    };

    return (
        <div className={`flex flex-col md:flex-row min-h-screen w-full bg-white transition-opacity  overflow-x-hidden`}>
            {/* Left Side - Testimonial Image */}
            <div
                className="hidden md:block w-full md:w-1/2 h-screen bg-cover bg-center bg-no-repeat fixed"
                style={{backgroundImage: `url(${testimonialImage})`}}
            />

            {/* Right Side - Login Form */}
            <div className="w-full md:w-1/2 flex flex-col items-center justify-center
                p-4 xs:p-5 sm:p-6 ml-0 md:ml-[50%] 
                bg-white dark:bg-acs-dark-surface transition-none">

                <div className="max-w-[90vw] xs:max-w-[85vw] sm:max-w-sm text-gray-900 dark:text-acs-dark-text">

                    {/* Logo Section */}
                    <div className="text-center space-y-fluid-2">
                        <img
                            src={ascendLogo}
                            alt="Ascend Logo"
                            className="w-[clamp(100px,15vw,160px)] mx-auto mb-fluid-2
                   transition-transform duration-300 hover:scale-105"
                        />
                        <h1 className="text-fluid-xl font-bold leading-tight @md:text-fluid-2xl">
                            Intelligent Logistics
                        </h1>
                        <p className="text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                            Take the search out of shipping
                        </p>
                    </div>

                    {/* Login Form */}
                    <form className="w-full mt-6" onSubmit={handleLogin}>
                        <div className="space-y-4">
                            {/* Email Input */}
                            <div>
                                <label className="block text-fluid-sm font-medium mb-fluid-1 dark:text-acs-dark-text">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    className="w-full p-fluid-1 text-fluid-sm border rounded-lg
                       bg-white dark:bg-acs-dark-surface
                       border-gray-300 dark:border-acs-dark-border
                       text-gray-900 dark:text-acs-dark-text
                       placeholder-gray-400 dark:placeholder-acs-dark-subtext
                       focus:ring-1 focus:ring-[#223688] dark:focus:ring-[#31C4AB]
                       transition-none"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* Password Input */}
                            <div>
                                <label className="block text-fluid-sm font-medium mb-fluid-1 dark:text-acs-dark-text">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    className="w-full p-fluid-1 text-fluid-sm border rounded-lg
                       bg-white dark:bg-acs-dark-surface
                       border-gray-300 dark:border-acs-dark-border
                       text-gray-900 dark:text-acs-dark-text
                       placeholder-gray-400 dark:placeholder-acs-dark-subtext
                       focus:ring-1 focus:ring-[#223688] dark:focus:ring-[#31C4AB]
                       transition-none"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>

                            {/* Remember Me & Forgot Password */}
                            <div className="flex flex-col sm:flex-row items-start justify-between gap-fluid-2">
                                <label className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        className=".    relative appearance-none cursor-pointer mx-auto block
                              w-[15px] h-[15px] rounded-sm 
                              border border-gray-300 dark:border-acs-dark-border 
                              bg-white dark:bg-acs-dark-surface 
                              transition-none
                              checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark
                               checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark
                              after:content-[''] after:absolute after:inset-0
                              after:flex after:items-center after:justify-center
                              checked:after:content-['✓']
                              checked:after:text-white checked:after:text-[12px] checked:after:font-bold
                            "
                                        checked={rememberMe}
                                        onChange={() => setRememberMe(!rememberMe)}
                                    />
                                    <span className="text-gray-600 dark:text-acs-dark-subtext block text-fluid-sm">
              Remember me
            </span>
                                </label>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!email) {
                                            notify.error({
                                                title: "Email required",
                                                message: "Please enter your email address."
                                            });
                                            return;
                                        }
                                        try {
                                            const response = await axios.post(
                                                `${process.env.REACT_APP_API_BASE_URL}/auth/user/forgetPwd?email=${encodeURIComponent(email)}`,
                                                JSON.stringify({email}),
                                                {
                                                    headers: {
                                                        "Content-Type": "application/json",
                                                        "Accept": "application/json"
                                                    }
                                                }
                                            );
                                            notify.success({
                                                title: "Success",
                                                message: response.data || "Password reset instructions sent."
                                            });
                                        } catch (error) {
                                            const errorMessage = error.response?.data?.content || error.message || "Failed to send reset instructions.";
                                            notify.error({title: "Error", message: errorMessage});
                                        }
                                    }}
                                    className="text-sm text-[#223688] dark:text-[#3B5CCC] hover:underline"
                                >
                                    Forgot password?
                                </button>
                            </div>

                            {/* Sign-in Button */}
                            <button
                                type="submit"
                                className="w-full p-2.5 text-sm rounded-lg font-medium
                     bg-[#223688] dark:bg-[#31C4AB]
                     text-white dark:text-[#1A1C23]
                     hover:bg-[#1c2c6d] dark:hover:bg-[#27b099]
                     transition-none flex justify-center items-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader compact message="Signing in..." color="text-white dark:text-white"
                                    />
                                ) : (
                                    "Sign in"
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Divider */}
                    <div className="relative my-fluid-3">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300 dark:border-acs-dark-border"/>
                        </div>
                        <div className="relative flex justify-center">
        <span
            className="px-fluid-1 bg-white dark:bg-acs-dark-surface text-gray-500 dark:text-acs-dark-subtext text-fluid-sm">
          or
        </span>
                        </div>
                    </div>

                    {/* SSO Login Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-fluid-1">
                        {[{logo: AppleLogo, name: "Apple"},
                            {logo: GoogleLogo, name: "Google"},
                            {logo: MicrosoftLogo, name: "Microsoft"},
                            {logo: SSOLogo, name: "SSO"}].map(({logo, name}) => (
                            <button
                                key={name}
                                className="flex items-center justify-center p-fluid-1.5 text-fluid-xs
                     border rounded-lg
                     border-gray-300 dark:border-acs-dark-border
                     bg-white dark:bg-acs-dark-hover
                     text-gray-800 dark:text-acs-dark-text
                     hover:bg-gray-50 dark:hover:bg-[#1f222a]
                     transition-none"
                            >
                                <img src={logo} alt={name} className="w-[1.2em] h-[1.2em] mr-fluid-1"/>
                                Sign in with {name}
                            </button>
                        ))}
                    </div>

                    {/* Sign Up Link */}
                    <p className="mt-fluid-3 text-center text-fluid-sm text-gray-600 dark:text-[#3B5CCC]">
                        Don’t have an account?{" "}
                        <button
                            
                            className="text-[#223688] dark:text-[#3B5CCC] hover:underline font-semibold transition-colors"
                        >
                            Sign up
                        </button>
                    </p>
                </div>
            </div>


            <AppToastContainer/>
        </div>
    );
};

export default LoginPage;